import { useState, useEffect } from 'react';
import api from '../api';
import {
  Crown,
  Save,
  RefreshCw, Download,
  ChevronDown,
  ChevronRight,
  Edit2,
  Check,
  X,
  Plus,
  Trash2,
  Clock,
  Calendar,
  Award,
  Zap,
  Play,
  AlertTriangle,
  Star,
  Gift,
  Link,
  Upload,
  Gamepad2,
  Search,
  Briefcase,
} from 'lucide-react';
import toast from 'react-hot-toast';

// Tier icons mapping
const TIER_ICONS = {
  'star-outline': Star,
  'star-half-outline': Star,
  'star': Star,
  'diamond': Crown,
  'trophy': Award,
};

export default function StatusSystem() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modified, setModified] = useState(false);
  
  // Expanded sections
  const [expandedTiers, setExpandedTiers] = useState([]);
  const [expandedSections, setExpandedSections] = useState(['tiers', 'resetMode', 'lifetime', 'multipliers', 'xpv2']);
  
  // Editing states
  const [editingLevel, setEditingLevel] = useState(null);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [newBenefit, setNewBenefit] = useState('');
  
  // Job status
  const [jobStatus, setJobStatus] = useState(null);
  const [runningJob, setRunningJob] = useState(null);
  
  // Tier sync status
  const [tierSyncStatus, setTierSyncStatus] = useState(null);
  const [syncingTiers, setSyncingTiers] = useState(false);
  const [initializingConfig, setInitializingConfig] = useState(false);

  // Game types for XP V2
  const [gameTypes, setGameTypes] = useState([]);
  const [loadingGameTypes, setLoadingGameTypes] = useState(false);
  const [gameTypeFilter, setGameTypeFilter] = useState('all'); // all, poker, cal_games
  const [gameTypeSearch, setGameTypeSearch] = useState('');

  const fetchGameTypes = async () => {
    try {
      setLoadingGameTypes(true);
      const res = await api.get('/admin/game-types');
      setGameTypes(res.data.games || []);
    } catch (err) {
      console.error('Failed to fetch game types:', err);
      toast.error('Failed to load game types: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingGameTypes(false);
    }
  };

  const updateGameType = async (gameId, newType, gameName) => {
    try {
      await api.put(`/admin/game-types/${gameId}`, { gameType: newType, gameName });
      setGameTypes(prev => prev.map(g => 
        g.gameId === gameId ? { ...g, gameType: newType } : g
      ));
      toast.success(`Updated ${gameName} to ${newType === 'poker' ? 'Poker' : 'Cal Games'}`);
    } catch (err) {
      toast.error('Failed to update game type');
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const [configRes, jobsRes, tierSyncRes] = await Promise.all([
        api.get('/status-system/config'),
        api.get('/status-system/jobs/status').catch(() => ({ data: null })),
        api.get('/admin/settings/tier-sync/status').catch(() => ({ data: null })),
      ]);
      setConfig(configRes.data);
      setJobStatus(jobsRes.data);
      setTierSyncStatus(tierSyncRes.data);
      setModified(false);
    } catch (err) {
      toast.error('Failed to load status system config');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  // Fetch game types when XP V2 section is expanded
  useEffect(() => {
    if (expandedSections.includes('xpv2') && gameTypes.length === 0) {
      fetchGameTypes();
    }
  }, [expandedSections]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/status-system/config', config);
      toast.success('Status system configuration saved');
      setModified(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (updates) => {
    setConfig(prev => ({ ...prev, ...updates }));
    setModified(true);
  };

  const updateTier = (tierKey, updates) => {
    setConfig(prev => ({
      ...prev,
      tiers: prev.tiers.map(t => 
        t.key === tierKey ? { ...t, ...updates } : t
      ),
    }));
    setModified(true);
  };

  const updateLevel = (tierKey, levelIndex, updates) => {
    setConfig(prev => ({
      ...prev,
      tiers: prev.tiers.map(t => {
        if (t.key !== tierKey) return t;
        const newLevels = [...t.levels];
        newLevels[levelIndex] = { ...newLevels[levelIndex], ...updates };
        return { ...t, levels: newLevels };
      }),
    }));
    setModified(true);
  };

  const addBenefit = (tierKey) => {
    if (!newBenefit.trim()) return;
    setConfig(prev => ({
      ...prev,
      tiers: prev.tiers.map(t => {
        if (t.key !== tierKey) return t;
        return { ...t, benefits: [...t.benefits, newBenefit.trim()] };
      }),
    }));
    setNewBenefit('');
    setModified(true);
  };

  const removeBenefit = (tierKey, index) => {
    setConfig(prev => ({
      ...prev,
      tiers: prev.tiers.map(t => {
        if (t.key !== tierKey) return t;
        return { ...t, benefits: t.benefits.filter((_, i) => i !== index) };
      }),
    }));
    setModified(true);
  };

  const updateBenefit = (tierKey, index, value) => {
    setConfig(prev => ({
      ...prev,
      tiers: prev.tiers.map(t => {
        if (t.key !== tierKey) return t;
        const newBenefits = [...t.benefits];
        newBenefits[index] = value;
        return { ...t, benefits: newBenefits };
      }),
    }));
    setModified(true);
  };

  const runJob = async (jobType, param = null) => {
    try {
      setRunningJob(jobType);
      let endpoint = '';
      switch (jobType) {
        case 'recalculate':
          endpoint = '/status-system/jobs/recalculate-all';
          break;
        case 'tierSync':
          endpoint = '/admin/settings/tier-sync/run';
          break;
        case 'yearEnd':
          endpoint = `/status-system/jobs/evaluate-year/${param}`;
          break;
        case 'riskCheck':
          endpoint = '/status-system/jobs/check-risk';
          break;
      }
      const res = await api.post(endpoint);
      if (res.data && res.data.success === false) {
        toast.error(res.data.error || res.data.message || 'Job failed');
      } else {
        toast.success(res.data.message || 'Job completed');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.details || 'Failed to run job');
    } finally {
      setRunningJob(null);
    }
  };

  const runTierSync = async () => {
    try {
      setSyncingTiers(true);
      const res = await api.post('/admin/settings/tier-sync/run');
      if (res.data.success) {
        toast.success(res.data.message || 'Tier sync completed');
        // Refresh tier sync status
        const statusRes = await api.get('/admin/settings/tier-sync/status').catch(() => ({ data: null }));
        setTierSyncStatus(statusRes.data);
      } else {
        toast.error(res.data.error || 'Tier sync failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to run tier sync');
    } finally {
      setSyncingTiers(false);
    }
  };

  const initializeTierConfig = async () => {
    try {
      setInitializingConfig(true);
      const res = await api.post('/admin/settings/tier-sync/initialize-config');
      if (res.data.success) {
        toast.success('Tier configuration initialized in TableCaptain');
      } else {
        toast.error(res.data.error || 'Failed to initialize tier config');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to initialize tier config');
    } finally {
      setInitializingConfig(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  const toggleTier = (tierKey) => {
    setExpandedTiers(prev =>
      prev.includes(tierKey) ? prev.filter(t => t !== tierKey) : [...prev, tierKey]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12 text-gray-400">
        Failed to load configuration
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Crown className="text-amber-500" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-white">Status System</h1>
            <p className="text-gray-400 text-sm">Configure tiers, benefits, and status rules</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchConfig}
            className="p-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
          <button
            onClick={handleSave}
            disabled={!modified || saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              modified
                ? 'bg-amber-500 text-black hover:bg-amber-400'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Unsaved changes banner */}
      {modified && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center justify-between">
          <span className="text-amber-400">You have unsaved changes</span>
          <button 
            onClick={fetchConfig} 
            className="text-gray-400 hover:text-white text-sm"
          >
            Discard changes
          </button>
        </div>
      )}

      {/* Tier Configuration */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => toggleSection('tiers')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-3">
            <Star className="text-amber-500" size={20} />
            <span className="text-lg font-semibold text-white">Tier Configuration</span>
            <span className="text-sm text-gray-500">({config.tiers.length} tiers)</span>
          </div>
          {expandedSections.includes('tiers') ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.includes('tiers') && (
          <div className="border-t border-gray-700 p-4 space-y-3">
            {config.tiers.map((tier) => (
              <div key={tier.key} className="bg-gray-700/50 rounded-lg overflow-hidden">
                {/* Tier Header */}
                <button
                  onClick={() => toggleTier(tier.key)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-600/50"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: tier.color + '30' }}
                    >
                      <Crown size={16} style={{ color: tier.color }} />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{tier.baseName}</span>
                        <input
                          type="color"
                          value={tier.color}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateTier(tier.key, { color: e.target.value });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-6 h-6 rounded cursor-pointer border-0"
                        />
                      </div>
                      <span className="text-gray-400 text-sm">
                        Levels {tier.levels[0].level}-{tier.levels[tier.levels.length - 1].level} &bull; {tier.benefits.length} benefits
                      </span>
                    </div>
                  </div>
                  {expandedTiers.includes(tier.key) ? (
                    <ChevronDown size={20} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={20} className="text-gray-400" />
                  )}
                </button>

                {/* Tier Details */}
                {expandedTiers.includes(tier.key) && (
                  <div className="border-t border-gray-600 p-4 space-y-4">
                    {/* Base Name */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Base Name</label>
                      <input
                        type="text"
                        value={tier.baseName}
                        onChange={(e) => updateTier(tier.key, { baseName: e.target.value })}
                        className="w-full max-w-xs bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                      />
                    </div>

                    {/* Level Names & XP */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Levels & XP Thresholds</label>
                      <div className="space-y-2">
                        {tier.levels.map((level, idx) => (
                          <div key={level.level} className="flex items-center gap-3 bg-gray-600/50 rounded-lg p-2">
                            <span className="text-gray-400 text-sm w-16">Level {level.level}</span>
                            {editingLevel?.tier === tier.key && editingLevel?.idx === idx ? (
                              <>
                                <input
                                  type="text"
                                  value={editingLevel.displayName}
                                  onChange={(e) => setEditingLevel(prev => ({ ...prev, displayName: e.target.value }))}
                                  className="flex-1 bg-gray-500 border border-gray-400 rounded px-2 py-1 text-white text-sm"
                                  placeholder="Display Name"
                                />
                                <input
                                  type="number"
                                  value={editingLevel.xpRequired}
                                  onChange={(e) => setEditingLevel(prev => ({ ...prev, xpRequired: Number(e.target.value) }))}
                                  className="w-24 bg-gray-500 border border-gray-400 rounded px-2 py-1 text-white text-sm"
                                  placeholder="XP"
                                />
                                <button
                                  onClick={() => {
                                    updateLevel(tier.key, idx, {
                                      displayName: editingLevel.displayName,
                                      xpRequired: editingLevel.xpRequired,
                                    });
                                    setEditingLevel(null);
                                  }}
                                  className="p-1 text-green-500 hover:text-green-400"
                                >
                                  <Check size={16} />
                                </button>
                                <button
                                  onClick={() => setEditingLevel(null)}
                                  className="p-1 text-gray-400 hover:text-white"
                                >
                                  <X size={16} />
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="flex-1 text-white">{level.displayName}</span>
                                <span className="text-amber-400 text-sm">{level.xpRequired.toLocaleString()} XP</span>
                                <button
                                  onClick={() => setEditingLevel({ tier: tier.key, idx, ...level })}
                                  className="p-1 text-gray-400 hover:text-amber-400"
                                >
                                  <Edit2 size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Benefits */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Benefits</label>
                      <div className="space-y-2">
                        {tier.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Gift size={14} className="text-green-500" />
                            {editingBenefit?.tier === tier.key && editingBenefit?.idx === idx ? (
                              <>
                                <input
                                  type="text"
                                  value={editingBenefit.value}
                                  onChange={(e) => setEditingBenefit(prev => ({ ...prev, value: e.target.value }))}
                                  className="flex-1 bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                                />
                                <button
                                  onClick={() => {
                                    updateBenefit(tier.key, idx, editingBenefit.value);
                                    setEditingBenefit(null);
                                  }}
                                  className="p-1 text-green-500 hover:text-green-400"
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  onClick={() => setEditingBenefit(null)}
                                  className="p-1 text-gray-400 hover:text-white"
                                >
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="flex-1 text-gray-300 text-sm">{benefit}</span>
                                <button
                                  onClick={() => setEditingBenefit({ tier: tier.key, idx, value: benefit })}
                                  className="p-1 text-gray-400 hover:text-amber-400"
                                >
                                  <Edit2 size={12} />
                                </button>
                                <button
                                  onClick={() => removeBenefit(tier.key, idx)}
                                  className="p-1 text-gray-400 hover:text-red-400"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                        {/* Add new benefit */}
                        <div className="flex items-center gap-2">
                          <Plus size={14} className="text-gray-500" />
                          <input
                            type="text"
                            value={expandedTiers.includes(tier.key) && newBenefit ? newBenefit : ''}
                            onChange={(e) => setNewBenefit(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addBenefit(tier.key)}
                            placeholder="Add benefit..."
                            className="flex-1 bg-transparent border-b border-gray-600 text-gray-300 text-sm py-1 focus:outline-none focus:border-amber-500"
                          />
                          <button
                            onClick={() => addBenefit(tier.key)}
                            disabled={!newBenefit.trim()}
                            className="text-amber-500 hover:text-amber-400 disabled:text-gray-600"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Lifetime Status */}
                    {tier.key !== 'vip' && (
                      <div className="pt-3 border-t border-gray-600">
                        <label className="block text-sm text-gray-400 mb-2">Lifetime Status</label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Years Required</label>
                            <input
                              type="number"
                              value={tier.lifetimeYearsRequired || ''}
                              onChange={(e) => updateTier(tier.key, { 
                                lifetimeYearsRequired: e.target.value ? Number(e.target.value) : null 
                              })}
                              placeholder="Not set"
                              className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Lifetime Display Name</label>
                            <input
                              type="text"
                              value={tier.lifetimeDisplayName || ''}
                              onChange={(e) => updateTier(tier.key, { lifetimeDisplayName: e.target.value })}
                              placeholder={`Lifetime ${tier.baseName}`}
                              className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Industry Tier (Hidden) */}
      <div className="bg-gray-800 rounded-xl overflow-hidden border border-purple-900/30">
        <button
          onClick={() => toggleSection('industry')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-3">
            <Briefcase className="text-purple-400" size={20} />
            <span className="font-medium">Industry Tier</span>
            <span className="px-2 py-0.5 bg-purple-900/40 text-purple-300 rounded text-xs">Hidden</span>
          </div>
          {expandedSections.includes('industry') ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>
        {expandedSections.includes('industry') && (
          <div className="p-4 border-t border-gray-700 space-y-4">
            <p className="text-sm text-gray-400">
              The Industry tier is a hidden tier for casino industry members. It overrides all tiers up to Boardmember, 
              but yields to Chairman. Players can be flagged via TableCaptain Role or admin override.
            </p>
            
            {/* Industry Tier Name & Colors */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Display Name</label>
                <input
                  type="text"
                  value={config.industryTier?.baseName || 'Industry'}
                  onChange={(e) => setConfig({
                    ...config,
                    industryTier: { ...config.industryTier, baseName: e.target.value }
                  })}
                  onBlur={() => setModified(true)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Icon</label>
                <select
                  value={config.industryTier?.icon || 'briefcase-outline'}
                  onChange={(e) => {
                    setConfig({
                      ...config,
                      industryTier: { ...config.industryTier, icon: e.target.value }
                    });
                    setModified(true);
                  }}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                >
                  <option value="briefcase-outline">Briefcase</option>
                  <option value="star-outline">Star</option>
                  <option value="diamond">Diamond</option>
                  <option value="trophy">Trophy</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Primary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config.industryTier?.color || '#6B21A8'}
                    onChange={(e) => {
                      setConfig({
                        ...config,
                        industryTier: { ...config.industryTier, color: e.target.value }
                      });
                      setModified(true);
                    }}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.industryTier?.color || '#6B21A8'}
                    onChange={(e) => {
                      setConfig({
                        ...config,
                        industryTier: { ...config.industryTier, color: e.target.value }
                      });
                      setModified(true);
                    }}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Accent Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config.industryTier?.accentColor || '#D4AF37'}
                    onChange={(e) => {
                      setConfig({
                        ...config,
                        industryTier: { ...config.industryTier, accentColor: e.target.value }
                      });
                      setModified(true);
                    }}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.industryTier?.accentColor || '#D4AF37'}
                    onChange={(e) => {
                      setConfig({
                        ...config,
                        industryTier: { ...config.industryTier, accentColor: e.target.value }
                      });
                      setModified(true);
                    }}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Industry Detection Settings */}
            <div className="pt-3 border-t border-gray-700">
              <h4 className="text-sm font-medium text-purple-300 mb-3">Detection Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">TableCaptain Role Value</label>
                  <input
                    type="number"
                    value={config.industryDetection?.tcRoleValue ?? ''}
                    onChange={(e) => {
                      setConfig({
                        ...config,
                        industryDetection: {
                          ...config.industryDetection,
                          tcRoleValue: e.target.value ? parseInt(e.target.value) : null
                        }
                      });
                      setModified(true);
                    }}
                    placeholder="Not configured"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Integer from TC Player.Role column</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Allow Admin Override</label>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.industryDetection?.allowAdminOverride !== false}
                      onChange={(e) => {
                        setConfig({
                          ...config,
                          industryDetection: {
                            ...config.industryDetection,
                            allowAdminOverride: e.target.checked
                          }
                        });
                        setModified(true);
                      }}
                      className="rounded bg-gray-700 border-gray-600 text-purple-500"
                    />
                    <span className="text-sm text-gray-300">Admins can flag players as Industry</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Industry Tier Benefits */}
            <div className="pt-3 border-t border-gray-700">
              <h4 className="text-sm font-medium text-purple-300 mb-2">Benefits</h4>
              <div className="space-y-2">
                {(config.industryTier?.benefits || []).map((benefit, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={benefit}
                      onChange={(e) => {
                        const newBenefits = [...(config.industryTier?.benefits || [])];
                        newBenefits[i] = e.target.value;
                        setConfig({
                          ...config,
                          industryTier: { ...config.industryTier, benefits: newBenefits }
                        });
                        setModified(true);
                      }}
                      className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    />
                    <button
                      onClick={() => {
                        const newBenefits = (config.industryTier?.benefits || []).filter((_, idx) => idx !== i);
                        setConfig({
                          ...config,
                          industryTier: { ...config.industryTier, benefits: newBenefits }
                        });
                        setModified(true);
                      }}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const newBenefits = [...(config.industryTier?.benefits || []), 'New Benefit'];
                    setConfig({
                      ...config,
                      industryTier: { ...config.industryTier, benefits: newBenefits }
                    });
                    setModified(true);
                  }}
                  className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                >
                  <Plus size={14} /> Add Benefit
                </button>
              </div>
            </div>

            {/* Preview */}
            <div className="pt-3 border-t border-gray-700">
              <h4 className="text-sm font-medium text-gray-400 mb-2">Preview</h4>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
                style={{
                  background: `linear-gradient(135deg, ${config.industryTier?.color || '#6B21A8'}, ${config.industryTier?.accentColor || '#D4AF37'})`,
                }}
              >
                <Briefcase size={16} className="text-white" />
                <span className="text-white font-medium text-sm">
                  {config.industryTier?.baseName || 'Industry'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reset Mode */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => toggleSection('resetMode')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-3">
            <Calendar className="text-amber-500" size={20} />
            <span className="text-lg font-semibold text-white">Status Reset Mode</span>
            <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">
              {config.resetMode === 'calendar_year' ? 'Calendar Year' : 
               config.resetMode === 'monthly' ? 'Monthly' :
               config.resetMode === 'quarterly' ? 'Quarterly' : 'Rolling 365'}
            </span>
          </div>
          {expandedSections.includes('resetMode') ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.includes('resetMode') && (
          <div className="border-t border-gray-700 p-4 space-y-4">
            {/* Mode Toggle */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => updateConfig({ resetMode: 'calendar_year' })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  config.resetMode === 'calendar_year'
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <Calendar size={24} className={config.resetMode === 'calendar_year' ? 'text-amber-500 mx-auto mb-2' : 'text-gray-400 mx-auto mb-2'} />
                <div className="text-white font-medium">Calendar Year</div>
                <div className="text-gray-400 text-sm">Resets Jan 1</div>
              </button>
              <button
                onClick={() => updateConfig({ resetMode: 'quarterly' })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  config.resetMode === 'quarterly'
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <Calendar size={24} className={config.resetMode === 'quarterly' ? 'text-amber-500 mx-auto mb-2' : 'text-gray-400 mx-auto mb-2'} />
                <div className="text-white font-medium">Quarterly</div>
                <div className="text-gray-400 text-sm">Resets every 3 months</div>
              </button>
              <button
                onClick={() => updateConfig({ resetMode: 'monthly' })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  config.resetMode === 'monthly'
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <Calendar size={24} className={config.resetMode === 'monthly' ? 'text-amber-500 mx-auto mb-2' : 'text-gray-400 mx-auto mb-2'} />
                <div className="text-white font-medium">Monthly</div>
                <div className="text-gray-400 text-sm">Resets each month</div>
              </button>
              <button
                onClick={() => updateConfig({ resetMode: 'rolling_365' })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  config.resetMode === 'rolling_365'
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <Clock size={24} className={config.resetMode === 'rolling_365' ? 'text-amber-500 mx-auto mb-2' : 'text-gray-400 mx-auto mb-2'} />
                <div className="text-white font-medium">Rolling 365 Days</div>
                <div className="text-gray-400 text-sm">Continuous window</div>
              </button>
            </div>

            {/* Calendar Year Settings */}
            {config.resetMode === 'calendar_year' && (
              <div className="bg-gray-700/50 rounded-lg p-4 space-y-3">
                <h4 className="text-white font-medium">Calendar Year Settings</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Reset Month</label>
                    <select
                      value={config.calendarYearSettings?.resetMonth || 1}
                      onChange={(e) => updateConfig({
                        calendarYearSettings: {
                          ...config.calendarYearSettings,
                          resetMonth: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    >
                      {['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'
                      ].map((month, i) => (
                        <option key={i} value={i + 1}>{month}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Reset Day</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={config.calendarYearSettings?.resetDay || 1}
                      onChange={(e) => updateConfig({
                        calendarYearSettings: {
                          ...config.calendarYearSettings,
                          resetDay: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Grace Months</label>
                    <input
                      type="number"
                      min="0"
                      max="24"
                      value={config.calendarYearSettings?.graceMonths || 12}
                      onChange={(e) => updateConfig({
                        calendarYearSettings: {
                          ...config.calendarYearSettings,
                          graceMonths: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Status valid for this many months after year ends</p>
                  </div>
                </div>
              </div>
            )}

            {/* Rolling 365 Settings */}
            {config.resetMode === 'rolling_365' && (
              <div className="bg-gray-700/50 rounded-lg p-4 space-y-3">
                <h4 className="text-white font-medium">Rolling Window Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Window Days</label>
                    <input
                      type="number"
                      min="30"
                      max="730"
                      value={config.rolling365Settings?.windowDays || 365}
                      onChange={(e) => updateConfig({
                        rolling365Settings: {
                          ...config.rolling365Settings,
                          windowDays: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Grace Days</label>
                    <input
                      type="number"
                      min="0"
                      max="90"
                      value={config.rolling365Settings?.graceDays ?? 30}
                      onChange={(e) => updateConfig({
                        rolling365Settings: {
                          ...config.rolling365Settings,
                          graceDays: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Warning period before status drops</p>
                  </div>
                </div>
              </div>
            )}

            {/* Monthly Settings */}
            {config.resetMode === 'monthly' && (
              <div className="bg-gray-700/50 rounded-lg p-4 space-y-3">
                <h4 className="text-white font-medium">Monthly Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Reset Day of Month</label>
                    <input
                      type="number"
                      min="1"
                      max="28"
                      value={config.monthlySettings?.resetDay || 1}
                      onChange={(e) => updateConfig({
                        monthlySettings: {
                          ...config.monthlySettings,
                          resetDay: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Day when new month period starts (1-28)</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Grace Days</label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={config.monthlySettings?.graceDays ?? 7}
                      onChange={(e) => updateConfig({
                        monthlySettings: {
                          ...config.monthlySettings,
                          graceDays: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Days after month ends before status drops</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quarterly Settings */}
            {config.resetMode === 'quarterly' && (
              <div className="bg-gray-700/50 rounded-lg p-4 space-y-3">
                <h4 className="text-white font-medium">Quarterly Settings</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Q1 Start Month</label>
                    <select
                      value={config.quarterlySettings?.resetMonth || 1}
                      onChange={(e) => updateConfig({
                        quarterlySettings: {
                          ...config.quarterlySettings,
                          resetMonth: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    >
                      {['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'
                      ].map((month, i) => (
                        <option key={i} value={i + 1}>{month}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">First month of Q1</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Reset Day</label>
                    <input
                      type="number"
                      min="1"
                      max="28"
                      value={config.quarterlySettings?.resetDay || 1}
                      onChange={(e) => updateConfig({
                        quarterlySettings: {
                          ...config.quarterlySettings,
                          resetDay: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Grace Days</label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      value={config.quarterlySettings?.graceDays ?? 14}
                      onChange={(e) => updateConfig({
                        quarterlySettings: {
                          ...config.quarterlySettings,
                          graceDays: Number(e.target.value),
                        },
                      })}
                      className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Days after quarter ends before status drops</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chairman Settings */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => toggleSection('chairman')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-3">
            <Crown className="text-amber-500" size={20} />
            <span className="text-lg font-semibold text-white">Chairman Mode</span>
            <span className={`px-2 py-0.5 rounded text-xs ${
              config.chairmanSettings?.mode === 'top_earner' ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700 text-gray-400'
            }`}>
              {config.chairmanSettings?.mode === 'top_earner' ? 'Top Earner' : 'XP Based'}
            </span>
          </div>
          {expandedSections.includes('chairman') ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.includes('chairman') && (
          <div className="border-t border-gray-700 p-4 space-y-4">
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="text-white font-medium mb-2">Chairman Qualification Mode</div>
              <div className="text-gray-400 text-sm mb-4">
                Choose how players achieve Chairman status
              </div>
              
              <div className="space-y-3">
                <label className="flex items-start gap-3 p-3 bg-gray-600/50 rounded-lg cursor-pointer hover:bg-gray-600">
                  <input
                    type="radio"
                    name="chairmanMode"
                    value="xp_based"
                    checked={config.chairmanSettings?.mode !== 'top_earner'}
                    onChange={() => updateConfig({
                      chairmanSettings: {
                        ...config.chairmanSettings,
                        mode: 'xp_based',
                      },
                    })}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-white font-medium">XP Based (Default)</div>
                    <div className="text-gray-400 text-sm">
                      Anyone who earns enough XP can reach Chairman. Multiple players can hold the title.
                    </div>
                  </div>
                </label>
                
                <label className="flex items-start gap-3 p-3 bg-gray-600/50 rounded-lg cursor-pointer hover:bg-gray-600">
                  <input
                    type="radio"
                    name="chairmanMode"
                    value="top_earner"
                    checked={config.chairmanSettings?.mode === 'top_earner'}
                    onChange={() => updateConfig({
                      chairmanSettings: {
                        ...config.chairmanSettings,
                        mode: 'top_earner',
                      },
                    })}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-white font-medium">Top Earner Only</div>
                    <div className="text-gray-400 text-sm">
                      Only the #1 XP earner for the current period holds the Chairman title. Creates competition!
                    </div>
                  </div>
                </label>
              </div>
            </div>
            
            {config.chairmanSettings?.mode === 'top_earner' && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-400 mb-2">
                  <Crown size={16} />
                  <span className="font-medium">Top Earner Mode Active</span>
                </div>
                <div className="text-gray-300 text-sm">
                  The player with the most XP in the current {config.resetMode === 'quarterly' ? 'quarter' : config.resetMode === 'monthly' ? 'month' : 'year'} will automatically receive Chairman status. 
                  When a new player takes the lead, they become the new Chairman and the previous holder returns to their XP-based tier.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lifetime Status Settings */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => toggleSection('lifetime')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-3">
            <Award className="text-amber-500" size={20} />
            <span className="text-lg font-semibold text-white">Lifetime Status</span>
            <span className={`px-2 py-0.5 rounded text-xs ${
              config.lifetimeStatus?.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'
            }`}>
              {config.lifetimeStatus?.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          {expandedSections.includes('lifetime') ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.includes('lifetime') && (
          <div className="border-t border-gray-700 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white font-medium">Enable Lifetime Status</div>
                <div className="text-gray-400 text-sm">Award permanent status after X years at a tier</div>
              </div>
              <button
                onClick={() => updateConfig({
                  lifetimeStatus: {
                    ...config.lifetimeStatus,
                    enabled: !config.lifetimeStatus?.enabled,
                  },
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.lifetimeStatus?.enabled ? 'bg-amber-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.lifetimeStatus?.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="text-white font-medium">Require Consecutive Years</div>
                <div className="text-gray-400 text-sm">Years must be back-to-back (no gaps)</div>
              </div>
              <button
                onClick={() => updateConfig({
                  lifetimeStatus: {
                    ...config.lifetimeStatus,
                    requireConsecutiveYears: !config.lifetimeStatus?.requireConsecutiveYears,
                  },
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.lifetimeStatus?.requireConsecutiveYears ? 'bg-amber-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.lifetimeStatus?.requireConsecutiveYears ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Lifetime Thresholds Summary */}
            {config.lifetimeStatus?.enabled && (
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">Lifetime Thresholds</h4>
                <div className="space-y-2">
                  {config.tiers.filter(t => t.lifetimeYearsRequired).map(tier => (
                    <div key={tier.key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tier.color }}
                        />
                        <span className="text-gray-300">{tier.lifetimeDisplayName || `Lifetime ${tier.baseName}`}</span>
                      </div>
                      <span className="text-gray-400">{tier.lifetimeYearsRequired} years at {tier.baseName}+</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* XP Multipliers */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => toggleSection('multipliers')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-3">
            <Zap className="text-amber-500" size={20} />
            <span className="text-lg font-semibold text-white">XP Multipliers</span>
          </div>
          {expandedSections.includes('multipliers') ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.includes('multipliers') && (
          <div className="border-t border-gray-700 p-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">XP per Hour</label>
                <input
                  type="number"
                  value={config.xpMultipliers?.xpPerHour || 100}
                  onChange={(e) => updateConfig({
                    xpMultipliers: {
                      ...config.xpMultipliers,
                      xpPerHour: Number(e.target.value),
                    },
                  })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">XP earned for each hour played</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">XP per Visit</label>
                <input
                  type="number"
                  value={config.xpMultipliers?.xpPerVisit ?? config.xpMultipliers?.xpPerSession ?? 50}
                  onChange={(e) => updateConfig({
                    xpMultipliers: {
                      ...config.xpMultipliers,
                      xpPerVisit: Number(e.target.value),
                    },
                  })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Bonus XP for each unique day visited</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">XP per Point</label>
                <input
                  type="number"
                  value={config.xpMultipliers?.xpPerPoint || 1}
                  onChange={(e) => updateConfig({
                    xpMultipliers: {
                      ...config.xpMultipliers,
                      xpPerPoint: Number(e.target.value),
                    },
                  })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">XP per loyalty point earned</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* XP System V2 - Progressive Rates */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => toggleSection('xpv2')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-3">
            <Zap className="text-green-500" size={20} />
            <span className="text-lg font-semibold text-white">XP System V2</span>
            {config.xpSystemV2?.enabled && (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-400 rounded">ACTIVE</span>
            )}
            {!config.xpSystemV2?.enabled && (
              <span className="px-2 py-0.5 text-xs font-medium bg-gray-500/20 text-gray-400 rounded">DISABLED</span>
            )}
          </div>
          {expandedSections.includes('xpv2') ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.includes('xpv2') && (
          <div className="border-t border-gray-700 p-4 space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between bg-gray-700/50 rounded-lg p-4">
              <div>
                <h4 className="text-white font-medium">Enable XP System V2</h4>
                <p className="text-gray-400 text-sm">Progressive tiered rates for Cal Games, fixed rate for Poker</p>
              </div>
              <button
                onClick={() => updateConfig({
                  xpSystemV2: {
                    ...config.xpSystemV2,
                    enabled: !config.xpSystemV2?.enabled,
                  },
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.xpSystemV2?.enabled ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.xpSystemV2?.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* V2 Settings - only show when enabled */}
            {config.xpSystemV2?.enabled && (
              <>
                {/* Visit Settings */}
                <div className="bg-gray-700/30 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                    <Clock size={16} className="text-blue-400" />
                    Visit Settings
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Visit Gap Tolerance (minutes)</label>
                      <input
                        type="number"
                        value={config.xpSystemV2?.visitGapToleranceMinutes ?? 60}
                        onChange={(e) => updateConfig({
                          xpSystemV2: {
                            ...config.xpSystemV2,
                            visitGapToleranceMinutes: Number(e.target.value),
                          },
                        })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      />
                      <p className="text-xs text-gray-500 mt-1">Sessions within this gap count as same visit</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Base XP per Visit</label>
                      <input
                        type="number"
                        value={config.xpSystemV2?.xpPerVisit ?? 25}
                        onChange={(e) => updateConfig({
                          xpSystemV2: {
                            ...config.xpSystemV2,
                            xpPerVisit: Number(e.target.value),
                          },
                        })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      />
                      <p className="text-xs text-gray-500 mt-1">XP awarded once per visit</p>
                    </div>
                  </div>
                </div>

                {/* Extended Play Bonus */}
                <div className="bg-gray-700/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <Gift size={16} className="text-purple-400" />
                      Extended Play Bonus
                    </h4>
                    <button
                      onClick={() => updateConfig({
                        xpSystemV2: {
                          ...config.xpSystemV2,
                          extendedPlayBonus: {
                            ...config.xpSystemV2?.extendedPlayBonus,
                            enabled: !config.xpSystemV2?.extendedPlayBonus?.enabled,
                          },
                        },
                      })}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        config.xpSystemV2?.extendedPlayBonus?.enabled ? 'bg-purple-500' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                          config.xpSystemV2?.extendedPlayBonus?.enabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {config.xpSystemV2?.extendedPlayBonus?.enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">XP Amount</label>
                        <input
                          type="number"
                          value={config.xpSystemV2?.extendedPlayBonus?.xpAmount ?? 25}
                          onChange={(e) => updateConfig({
                            xpSystemV2: {
                              ...config.xpSystemV2,
                              extendedPlayBonus: {
                                ...config.xpSystemV2?.extendedPlayBonus,
                                xpAmount: Number(e.target.value),
                              },
                            },
                          })}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Minutes Required</label>
                        <input
                          type="number"
                          value={config.xpSystemV2?.extendedPlayBonus?.minutesRequired ?? 120}
                          onChange={(e) => updateConfig({
                            xpSystemV2: {
                              ...config.xpSystemV2,
                              extendedPlayBonus: {
                                ...config.xpSystemV2?.extendedPlayBonus,
                                minutesRequired: Number(e.target.value),
                              },
                            },
                          })}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">Bonus awarded every X minutes of play</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Game Type XP Rates */}
                <div className="bg-gray-700/30 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                    <Star size={16} className="text-amber-400" />
                    Game Type XP Rates
                  </h4>
                  
                  {/* Poker Rate */}
                  <div className="mb-4 pb-4 border-b border-gray-600">
                    <label className="block text-sm text-gray-400 mb-1">Poker XP per Hour (Fixed Rate)</label>
                    <input
                      type="number"
                      value={config.xpSystemV2?.pokerXpPerHour ?? 50}
                      onChange={(e) => updateConfig({
                        xpSystemV2: {
                          ...config.xpSystemV2,
                          pokerXpPerHour: Number(e.target.value),
                        },
                      })}
                      className="w-48 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">All poker games earn this fixed rate</p>
                  </div>

                  {/* Cal Games Tiers */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="block text-sm text-gray-400">Cal Games XP Tiers (Progressive Rates)</label>
                        <p className="text-xs text-gray-500">Higher rates based on average bet per session</p>
                      </div>
                      <button
                        onClick={() => {
                          const tiers = [...(config.xpSystemV2?.calGamesXpTiers || [])];
                          const lastTier = tiers[tiers.length - 1];
                          // Support both maxAverageBet (new) and maxMinutes (legacy)
                          const lastMax = lastTier?.maxAverageBet ?? lastTier?.maxMinutes ?? 0;
                          const newMaxBet = lastMax === Infinity ? 999999 : lastMax + 25;
                          tiers.push({ maxAverageBet: newMaxBet, xpPerHour: (lastTier?.xpPerHour || 100) + 100 });
                          updateConfig({
                            xpSystemV2: {
                              ...config.xpSystemV2,
                              calGamesXpTiers: tiers,
                            },
                          });
                        }}
                        className="px-3 py-1 text-sm bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 flex items-center gap-1"
                      >
                        <Plus size={14} />
                        Add Tier
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 px-2">
                        <div className="col-span-5">Average Bet Range</div>
                        <div className="col-span-5">XP per Hour</div>
                        <div className="col-span-2"></div>
                      </div>
                      {(config.xpSystemV2?.calGamesXpTiers || []).map((tier, index) => {
                        // Calculate the lower bound (previous tier's max + 1, or 0 for first tier)
                        // Support both maxAverageBet (new) and maxMinutes (legacy for backward compatibility)
                        const tiers = config.xpSystemV2?.calGamesXpTiers || [];
                        const prevTier = index > 0 ? tiers[index - 1] : null;
                        const prevMax = prevTier ? (prevTier.maxAverageBet ?? prevTier.maxMinutes ?? 0) : 0;
                        const lowerBound = prevMax === Infinity ? 0 : prevMax + 1;
                        const currentMax = tier.maxAverageBet ?? tier.maxMinutes ?? 0;
                        const isLastTier = currentMax === Infinity || currentMax >= 999999;
                        
                        return (
                        <div key={index} className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-5">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400 text-sm w-12 text-right">${lowerBound}</span>
                              <span className="text-gray-500 text-sm">-</span>
                              {isLastTier ? (
                                <span className="bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-amber-400 text-sm">&#x221E;</span>
                              ) : (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={currentMax === Infinity ? '' : currentMax}
                                  onChange={(e) => {
                                    const newTiers = [...(config.xpSystemV2?.calGamesXpTiers || [])];
                                    const value = e.target.value === '' ? Infinity : Number(e.target.value);
                                    // Remove legacy maxMinutes if present, use maxAverageBet
                                    const updatedTier = { ...newTiers[index] };
                                    delete updatedTier.maxMinutes;
                                    updatedTier.maxAverageBet = value;
                                    newTiers[index] = updatedTier;
                                    updateConfig({
                                      xpSystemV2: {
                                        ...config.xpSystemV2,
                                        calGamesXpTiers: newTiers,
                                      },
                                    });
                                  }}
                                  className="w-24 bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm"
                                />
                              )}
                              <span className="text-gray-500 text-sm">avg bet</span>
                            </div>
                          </div>
                          <div className="col-span-5">
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={tier.xpPerHour}
                                onChange={(e) => {
                                  const tiers = [...(config.xpSystemV2?.calGamesXpTiers || [])];
                                  tiers[index] = { ...tiers[index], xpPerHour: Number(e.target.value) };
                                  updateConfig({
                                    xpSystemV2: {
                                      ...config.xpSystemV2,
                                      calGamesXpTiers: tiers,
                                    },
                                  });
                                }}
                                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm"
                              />
                              <span className="text-gray-500 text-sm">XP/hr</span>
                            </div>
                          </div>
                          <div className="col-span-2 flex justify-end">
                            {(config.xpSystemV2?.calGamesXpTiers || []).length > 1 && (
                              <button
                                onClick={() => {
                                  const tiers = [...(config.xpSystemV2?.calGamesXpTiers || [])];
                                  tiers.splice(index, 1);
                                  updateConfig({
                                    xpSystemV2: {
                                      ...config.xpSystemV2,
                                      calGamesXpTiers: tiers,
                                    },
                                  });
                                }}
                                className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    How XP V2 Works
                  </h4>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>&bull; <strong>Poker Games:</strong> Earn fixed {config.xpSystemV2?.pokerXpPerHour ?? 50} XP/hour</li>
                    <li>&bull; <strong>Cal Games:</strong> Earn XP based on average bet per session - higher bets earn higher XP rates</li>
                    <li>&bull; <strong>Visit:</strong> Sessions within {config.xpSystemV2?.visitGapToleranceMinutes ?? 60} minutes count as the same visit</li>
                    <li>&bull; <strong>Base XP:</strong> {config.xpSystemV2?.xpPerVisit ?? 25} XP awarded once per visit</li>
                    {config.xpSystemV2?.extendedPlayBonus?.enabled && (
                      <li>&bull; <strong>Extended Play:</strong> +{config.xpSystemV2?.extendedPlayBonus?.xpAmount ?? 25} XP every {config.xpSystemV2?.extendedPlayBonus?.minutesRequired ?? 120} minutes</li>
                    )}
                  </ul>
                </div>

                {/* Game Type Management */}
                <div className="bg-gray-700/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <Gamepad2 size={16} className="text-cyan-400" />
                      Game Type Classification
                    </h4>
                    <button
                      onClick={fetchGameTypes}
                      disabled={loadingGameTypes}
                      className="px-3 py-1 text-sm bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 flex items-center gap-1"
                    >
                      <RefreshCw size={14} className={loadingGameTypes ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                  </div>
                  <p className="text-gray-400 text-sm mb-4">
                    Classify each game as Poker (fixed rate) or Cal Games (progressive rates)
                  </p>

                  {/* Filters */}
                  <div className="flex gap-3 mb-4">
                    <div className="flex-1 relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        placeholder="Search games..."
                        value={gameTypeSearch}
                        onChange={(e) => setGameTypeSearch(e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-9 pr-3 py-2 text-white text-sm"
                      />
                    </div>
                    <select
                      value={gameTypeFilter}
                      onChange={(e) => setGameTypeFilter(e.target.value)}
                      className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="all">All Types</option>
                      <option value="poker">Poker Only</option>
                      <option value="cal_games">Cal Games Only</option>
                    </select>
                  </div>

                  {/* Games List */}
                  {loadingGameTypes ? (
                    <div className="text-center py-8">
                      <RefreshCw size={24} className="animate-spin text-gray-500 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">Loading games...</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {gameTypes
                        .filter(g => {
                          const matchesSearch = !gameTypeSearch || 
                            g.gameName?.toLowerCase().includes(gameTypeSearch.toLowerCase()) ||
                            g.gameNameOriginal?.toLowerCase().includes(gameTypeSearch.toLowerCase());
                          const matchesFilter = gameTypeFilter === 'all' || g.gameType === gameTypeFilter;
                          return matchesSearch && matchesFilter;
                        })
                        .sort((a, b) => {
                          // Sort active games first, then alphabetically
                          if (a.isActive !== b.isActive) return b.isActive - a.isActive;
                          return (a.gameName || '').localeCompare(b.gameName || '');
                        })
                        .map(game => (
                          <div 
                            key={game.gameId} 
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              game.isActive ? 'bg-gray-600/50' : 'bg-gray-700/30'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {game.isActive && (
                                <span className="w-2 h-2 rounded-full bg-green-500" title="Currently Active" />
                              )}
                              <div>
                                <span className="text-white text-sm font-medium">{game.gameName}</span>
                                {game.isActive && game.playing > 0 && (
                                  <span className="ml-2 text-xs text-gray-400">
                                    ({game.playing} playing)
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateGameType(game.gameId, 'poker', game.gameName)}
                                className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                                  game.gameType === 'poker'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-600 text-gray-400 hover:bg-gray-500'
                                }`}
                              >
                                Poker
                              </button>
                              <button
                                onClick={() => updateGameType(game.gameId, 'cal_games', game.gameName)}
                                className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                                  game.gameType === 'cal_games'
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-gray-600 text-gray-400 hover:bg-gray-500'
                                }`}
                              >
                                Cal Games
                              </button>
                            </div>
                          </div>
                        ))}
                      {gameTypes.length === 0 && !loadingGameTypes && (
                        <div className="text-center py-8 text-gray-500">
                          No games found. Games will appear here once they're active in TableCaptain.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Summary */}
                  {gameTypes.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-600 flex gap-6 text-sm">
                      <div className="text-gray-400">
                        <span className="text-blue-400 font-medium">
                          {gameTypes.filter(g => g.gameType === 'poker').length}
                        </span> Poker
                      </div>
                      <div className="text-gray-400">
                        <span className="text-amber-400 font-medium">
                          {gameTypes.filter(g => g.gameType === 'cal_games').length}
                        </span> Cal Games
                      </div>
                      <div className="text-gray-400">
                        <span className="text-green-400 font-medium">
                          {gameTypes.filter(g => g.isActive).length}
                        </span> Active Now
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Data Start Dates */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => toggleSection('startDates')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-3">
            <Calendar className="text-blue-500" size={20} />
            <span className="text-lg font-semibold text-white">Data Start Dates</span>
          </div>
          {expandedSections.includes('startDates') ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.includes('startDates') && (
          <div className="border-t border-gray-700 p-4 space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle size={18} className="text-blue-400 mt-0.5" />
              <div className="text-blue-200 text-sm">
                These dates control which player activity counts toward XP and achievement calculations. Activity before these dates is ignored. Leave empty to count all historical activity.
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-700/30 rounded-lg p-4">
                <label className="block text-sm text-gray-400 mb-1">XP Start Date</label>
                <input
                  type="date"
                  value={config.xpStartDate || ''}
                  onChange={(e) => updateConfig({ xpStartDate: e.target.value || null })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white [color-scheme:dark]"
                />
                <p className="text-xs text-gray-500 mt-1">Only activity on or after this date counts toward XP calculations</p>
              </div>
              <div className="bg-gray-700/30 rounded-lg p-4">
                <label className="block text-sm text-gray-400 mb-1">Achievement Start Date</label>
                <input
                  type="date"
                  value={config.achievementStartDate || ''}
                  onChange={(e) => updateConfig({ achievementStartDate: e.target.value || null })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white [color-scheme:dark]"
                />
                <p className="text-xs text-gray-500 mt-1">Only activity on or after this date counts toward achievement progress</p>
              </div>
            </div>

            <div className="text-xs text-gray-500">
              Default: 2026-01-01 (if not set). Changes take effect on next status recalculation.
            </div>
          </div>
        )}
      </div>

      {/* Scheduled Jobs */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => toggleSection('jobs')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-3">
            <Play className="text-amber-500" size={20} />
            <span className="text-lg font-semibold text-white">Scheduled Jobs</span>
          </div>
          {expandedSections.includes('jobs') ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.includes('jobs') && (
          <div className="border-t border-gray-700 p-4 space-y-4">
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle size={18} className="text-yellow-500 mt-0.5" />
              <div className="text-yellow-200 text-sm">
                Manual job execution should only be used for testing or recovery. Jobs run automatically on schedule.
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Recalculate All */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw size={18} className="text-blue-400" />
                  <span className="text-white font-medium">Recalculate All</span>
                </div>
                <p className="text-gray-400 text-sm mb-3">
                  Recalculate status for all active members
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Schedule: Daily at 3:00 AM PT
                </p>
                <button
                  onClick={() => runJob('recalculate')}
                  disabled={runningJob === 'recalculate'}
                  className="w-full py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 disabled:opacity-50"
                >
                  {runningJob === 'recalculate' ? 'Running...' : 'Run Now'}
                </button>
              </div>

              {/* Tier Sync to TableCaptain */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw size={18} className="text-green-400" />
                  <span className="text-white font-medium">Tier Sync</span>
                </div>
                <p className="text-gray-400 text-sm mb-3">
                  Sync all tiers to TableCaptain
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Schedule: Daily at 3:00 AM PT
                </p>
                <button
                  onClick={() => runJob('tierSync')}
                  disabled={runningJob === 'tierSync'}
                  className="w-full py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 disabled:opacity-50"
                >
                  {runningJob === 'tierSync' ? 'Running...' : 'Sync Now'}
                </button>
              </div>

              {/* Year-End Evaluation */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Award size={18} className="text-amber-400" />
                  <span className="text-white font-medium">Year-End Eval</span>
                </div>
                <p className="text-gray-400 text-sm mb-3">
                  Evaluate lifetime status for a year
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Schedule: Jan 2 at 1:00 AM PT
                </p>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Year"
                    min="2020"
                    max={new Date().getFullYear()}
                    defaultValue={new Date().getFullYear() - 1}
                    id="yearEndInput"
                    className="flex-1 bg-gray-600 border border-gray-500 rounded px-2 py-2 text-white text-sm"
                  />
                  <button
                    onClick={() => {
                      const year = document.getElementById('yearEndInput').value;
                      if (year) runJob('yearEnd', year);
                    }}
                    disabled={runningJob === 'yearEnd'}
                    className="px-3 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 disabled:opacity-50"
                  >
                    {runningJob === 'yearEnd' ? '...' : 'Run'}
                  </button>
                </div>
              </div>

              {/* Status Risk Check */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={18} className="text-red-400" />
                  <span className="text-white font-medium">Risk Check</span>
                </div>
                <p className="text-gray-400 text-sm mb-3">
                  Check for users at risk of status drop
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Schedule: Every 6 hours (rolling mode only)
                </p>
                <button
                  onClick={() => runJob('riskCheck')}
                  disabled={runningJob === 'riskCheck' || config.resetMode !== 'rolling_365'}
                  className="w-full py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50"
                >
                  {runningJob === 'riskCheck' ? 'Running...' : 'Run Now'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
